import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Grid3x3, Plus, Save, X, RefreshCw } from "lucide-react";
import { useTrackView } from "@/lib/analyticsTracker";

/**
 * ServiceDashboard — the SLX Service module guest-preferences view.
 *
 * Two modes:
 *   • "guest"   — pick one guest → whiteboard-style category cards with
 *                 tap-to-edit free-text.
 *   • "compare" — multi-select guests + multi-select categories → matrix
 *                 grid so the crew can see "what to prepare" for
 *                 several guests in one screen (e.g. Breakfast for Oleg
 *                 & Aliya).
 *
 * Access: Pantry + Manager+ (backend enforces via _require_service_role).
 * Non-regular guests are trip-scoped. Linked Regulars transparently read/write
 * their persistent Regulars profile so their service preferences follow them.
 */
export default function ServiceDashboard({ guests = [] }) {
  useTrackView("service_dashboard");
  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState([]); // full set for the guest / compare selection
  const [resolvedGuests, setResolvedGuests] = useState(guests || []);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("guest"); // guest | compare
  const [selGuest, setSelGuest] = useState("");
  const [cmpGuestIds, setCmpGuestIds] = useState([]);
  const [cmpCategoryIds, setCmpCategoryIds] = useState([]);
  const [editingKey, setEditingKey] = useState(null); // `${guest}:${cat}`
  const [editingText, setEditingText] = useState("");

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/admin/preference-categories");
      setCategories(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load categories");
    }
  };

  const loadPrefs = async (guestIds = [], categoryIds = []) => {
    setLoading(true);
    try {
      const params = {};
      if (guestIds.length) params.guest_ids = guestIds.join(",");
      if (categoryIds.length) params.category_ids = categoryIds.join(",");
      const { data } = await api.get("/admin/service/preferences", { params });
      setPrefs(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load preferences");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCategories(); }, []);

  // Always ask the server for the Service view of onboard guests. It enriches
  // linked Regulars with allergies/dietary/core preference notes while keeping
  // the existing `guests` prop as an offline/fallback source for the Pantry UI.
  useEffect(() => {
    setResolvedGuests(guests || []);
    api.get("/admin/service/guests")
      .then(({ data }) => { if (Array.isArray(data)) setResolvedGuests(data); })
      .catch(() => {});
  }, [guests]);

  // When the selected guest / compare selection changes, refetch the
  // relevant slice of preferences so we're not holding the whole set
  // in memory.
  useEffect(() => {
    if (view === "guest" && selGuest) loadPrefs([selGuest]);
    else if (view === "compare" && cmpGuestIds.length) loadPrefs(cmpGuestIds, cmpCategoryIds);
    else setPrefs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selGuest, cmpGuestIds, cmpCategoryIds]);

  const activeCategories = useMemo(() => categories.filter((c) => c.active !== false), [categories]);
  const displayGuests = resolvedGuests?.length ? resolvedGuests : (guests || []);
  const guestMap = useMemo(() => Object.fromEntries((displayGuests || []).map((g) => [g.guest_id || g.id, g])), [displayGuests]);
  const prefMap = useMemo(() => {
    const m = {};
    for (const p of prefs) m[`${p.guest_id}:${p.category_id}`] = p;
    return m;
  }, [prefs]);

  const startEdit = (guestId, categoryId) => {
    const key = `${guestId}:${categoryId}`;
    const existing = prefMap[key];
    setEditingKey(key);
    setEditingText(existing?.text || "");
  };
  const cancelEdit = () => { setEditingKey(null); setEditingText(""); };
  const saveEdit = async (guestId, categoryId) => {
    try {
      await api.put("/admin/service/preferences", { guest_id: guestId, category_id: categoryId, text: editingText });
      toast.success("Saved");
      cancelEdit();
      // Refresh just this slice
      if (view === "guest" && selGuest) loadPrefs([selGuest]);
      else loadPrefs(cmpGuestIds, cmpCategoryIds);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save");
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-6" data-testid="service-dashboard">
      {/* Sub-header: view switcher + guest picker */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-primary/60 mb-1">Service module</div>
          <h2 className="font-display text-2xl sm:text-3xl">Guest preferences</h2>
        </div>
        <div className="inline-flex border border-border rounded-full overflow-hidden" data-testid="service-view-toggle">
          <button
            onClick={() => setView("guest")}
            className={`px-4 h-10 text-sm inline-flex items-center gap-2 ${view === "guest" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            data-testid="service-view-guest"
          ><Users className="h-3.5 w-3.5" /> Single guest</button>
          <button
            onClick={() => setView("compare")}
            className={`px-4 h-10 text-sm inline-flex items-center gap-2 ${view === "compare" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            data-testid="service-view-compare"
          ><Grid3x3 className="h-3.5 w-3.5" /> Compare guests</button>
        </div>
      </div>

      {view === "guest" ? (
        <SingleGuestView
          guests={displayGuests}
          guest={guestMap[selGuest]}
          categories={activeCategories}
          prefMap={prefMap}
          selGuest={selGuest}
          setSelGuest={setSelGuest}
          editingKey={editingKey}
          editingText={editingText}
          setEditingText={setEditingText}
          startEdit={startEdit}
          cancelEdit={cancelEdit}
          saveEdit={saveEdit}
          loading={loading}
        />
      ) : (
        <CompareView
          guests={displayGuests}
          categories={activeCategories}
          prefMap={prefMap}
          cmpGuestIds={cmpGuestIds}
          setCmpGuestIds={setCmpGuestIds}
          cmpCategoryIds={cmpCategoryIds}
          setCmpCategoryIds={setCmpCategoryIds}
          editingKey={editingKey}
          editingText={editingText}
          setEditingText={setEditingText}
          startEdit={startEdit}
          cancelEdit={cancelEdit}
          saveEdit={saveEdit}
          loading={loading}
        />
      )}
    </div>
  );
}

function SingleGuestView({ guests, guest, categories, prefMap, selGuest, setSelGuest, editingKey, editingText, setEditingText, startEdit, cancelEdit, saveEdit, loading }) {
  return (
    <>
      <div className="mb-6">
        <label className="overline text-primary/60 block mb-2">Guest</label>
        <select
          value={selGuest}
          onChange={(e) => setSelGuest(e.target.value)}
          className="w-full max-w-md h-11 border border-border bg-background px-3 text-sm"
          data-testid="service-guest-picker"
        >
          <option value="">— select guest —</option>
          {guests.map((g) => (
            <option key={g.guest_id || g.id} value={g.guest_id || g.id}>
              {g.name}{g.cabin_name ? ` · ${g.cabin_name}` : ""}
            </option>
          ))}
        </select>
      </div>

      {!selGuest ? (
        <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          Pick a guest to see their preference sheet.
        </div>
      ) : (
        <>
          {guest?.is_regular && (
            <div className="mb-5 border border-accent/40 bg-accent/5 p-4" data-testid="service-regular-profile">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <div className="overline text-accent">Regular guest · synced from Regulars</div>
                  <div className="text-sm text-muted-foreground mt-1">Category entries below are persistent. Changes made here are saved back to this guest's Regulars profile and reused on future trips.</div>
                </div>
                {guest?.regular_profile?.role && <span className="text-xs border border-border px-2 py-1 uppercase tracking-wider">{guest.regular_profile.role}</span>}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <ServiceProfileFact label="Allergies" value={guest?.regular_profile?.allergies} danger />
                <ServiceProfileFact label="Dietary" value={guest?.regular_profile?.dietary} />
                <ServiceProfileFact label="Preference notes" value={guest?.regular_profile?.preferences_notes} />
              </div>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="service-guest-sheet">
          {categories.map((c) => {
            const key = `${selGuest}:${c.id}`;
            const p = prefMap[key];
            const isEditing = editingKey === key;
            return (
              <div key={c.id} className="border border-border bg-card p-4 flex flex-col min-h-[180px]" data-testid={`service-cell-${c.id}`}>
                <div
                  className="overline text-[10px] tracking-widest pb-2 mb-3 border-b"
                  style={{ color: c.color || undefined, borderColor: c.color || undefined }}
                >{c.name}</div>
                {isEditing ? (
                  <div className="flex flex-col gap-2 flex-1">
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="flex-1 min-h-[100px] border border-border p-2 text-sm bg-background"
                      placeholder="Notes / preferences…"
                      autoFocus
                      data-testid={`service-cell-input-${c.id}`}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(selGuest, c.id)} data-testid={`service-cell-save-${c.id}`}>
                        <Save className="h-3 w-3 mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        <X className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => startEdit(selGuest, c.id)}
                    className="flex-1 text-left text-sm whitespace-pre-wrap hover:bg-muted/30 -m-1 p-1 transition-colors"
                    data-testid={`service-cell-edit-${c.id}`}
                  >
                    {p?.text ? (
                      <span>{p.text}</span>
                    ) : (
                      <span className="text-muted-foreground/60 inline-flex items-center gap-1 text-xs">
                        <Plus className="h-3 w-3" /> Tap to add
                      </span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}
      {loading && selGuest && (
        <div className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-2">
          <RefreshCw className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
    </>
  );
}

function ServiceProfileFact({ label, value, danger = false }) {
  return (
    <div className={`border p-3 ${danger && value ? "border-rose-500/50 bg-rose-500/5" : "border-border bg-card/50"}`}>
      <div className={`overline text-[9px] ${danger && value ? "text-rose-500" : "text-muted-foreground"}`}>{label}</div>
      <div className={`text-sm mt-1 whitespace-pre-wrap ${!value ? "text-muted-foreground/60" : ""}`}>{value || "Not recorded"}</div>
    </div>
  );
}

function CompareView({ guests, categories, prefMap, cmpGuestIds, setCmpGuestIds, cmpCategoryIds, setCmpCategoryIds, editingKey, editingText, setEditingText, startEdit, cancelEdit, saveEdit, loading }) {
  const toggleId = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const shownCats = cmpCategoryIds.length ? categories.filter((c) => cmpCategoryIds.includes(c.id)) : categories;

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <div>
          <div className="overline text-primary/60 mb-2">Guests <span className="text-xs normal-case tracking-normal text-muted-foreground">· pick several</span></div>
          <div className="flex flex-wrap gap-2" data-testid="service-compare-guests">
            {guests.map((g) => {
              const id = g.guest_id || g.id;
              const active = cmpGuestIds.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => toggleId(cmpGuestIds, setCmpGuestIds, id)}
                  className={`px-3 h-9 text-xs border rounded-full transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                  data-testid={`service-compare-guest-${id}`}
                >{g.name}{g.cabin_name ? ` · ${g.cabin_name}` : ""}</button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="overline text-primary/60 mb-2">Categories <span className="text-xs normal-case tracking-normal text-muted-foreground">· leave empty for all</span></div>
          <div className="flex flex-wrap gap-2" data-testid="service-compare-cats">
            {categories.map((c) => {
              const active = cmpCategoryIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleId(cmpCategoryIds, setCmpCategoryIds, c.id)}
                  className={`px-3 h-9 text-xs border rounded-full transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                  style={active ? undefined : { color: c.color || undefined }}
                >{c.name}</button>
              );
            })}
          </div>
        </div>
      </div>

      {cmpGuestIds.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
          Pick one or more guests to see a compare grid.
        </div>
      ) : (
        <div className="overflow-x-auto border border-border" data-testid="service-compare-grid">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 text-left overline text-[10px] w-40">Category</th>
                {cmpGuestIds.map((gid) => (
                  <th key={gid} className="p-3 text-left overline text-[10px]">
                    {guests.find((x) => (x.guest_id || x.id) === gid)?.name || gid}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownCats.map((c) => (
                <tr key={c.id} className="border-t border-border align-top">
                  <td className="p-3 font-medium" style={{ color: c.color || undefined }}>{c.name}</td>
                  {cmpGuestIds.map((gid) => {
                    const key = `${gid}:${c.id}`;
                    const p = prefMap[key];
                    const isEditing = editingKey === key;
                    return (
                      <td key={gid} className="p-3 min-w-[180px]">
                        {isEditing ? (
                          <div className="flex flex-col gap-2">
                            <textarea
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              className="w-full min-h-[70px] border border-border p-2 text-sm bg-background"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => saveEdit(gid, c.id)}><Save className="h-3 w-3 mr-1" /> Save</Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit}><X className="h-3 w-3" /></Button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(gid, c.id)}
                            className="text-left w-full whitespace-pre-wrap hover:bg-muted/30 -m-1 p-1"
                          >
                            {p?.text || <span className="text-muted-foreground/60 text-xs">— empty —</span>}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {loading && cmpGuestIds.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-2">
          <RefreshCw className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
    </>
  );
}
